/*
 * YourXI pitch library
 * --------------------------------------------------------------------------
 * Formats, formations, pitch markings and print colour styles for the
 * Bespoke XI wizard. No dependencies. Works as a <script> (window.YourXIPitches)
 * or as a CommonJS module.
 *
 *   FORMATS                         formats with formations (slot coords 0-1)
 *   PITCH_STYLES                    classic, night, grass, clubTint(hex), chalkboard
 *   getStyle(styleId, colour?)      -> resolved style object
 *   pitchSVG(formatId, styleId, {width, height, colour, id})
 *                                   -> '<g>' string of pitch markings sized to width x height,
 *                                      origin top-left. Wrap it in a translate() to place it.
 *   paperSVG(styleId, {width, height, colour})
 *                                   -> '<g>' full-page paper (background) for the style
 *   pitchGeometry(formatId, {width, height})
 *                                   -> { project(u,v) metres -> [x,y], corners, widthAt(y), dims }
 *   layoutSlots(formatId, formationId, {width, height})
 *                                   -> [{x, y, s, role, fine}] in box pixels, s = shirt scale
 *   getFormat(id), getFormation(formatId, formationId)
 *
 * COORDINATE SYSTEM
 * The pitch is the original master drawing (traced from the InDesign masters,
 * see "Geometry" below), scaled uniformly to fit inside a box (width x height),
 * anchored bottom-centre. Default box = the wizard's print pitch area: 780 x 758
 * at (60, 262) on the 900 x 1200 print. His shape is 1478 x 1538 pt, so it fills
 * the box height exactly (scale 0.49277): outline x 85.8-814.2, top goal line
 * y 262, bottom goal line y 1020, footer band (1020-1200) untouched.
 * All formats share his outline, taper, line weights and colour; only the
 * markings change.
 *
 * Slot coordinates (FORMATS[..].formations[..].slots) are normalised 0-1 to that
 * box: x 0 = left edge of the box (bottom-left corner), 1 = right edge;
 * y 0 = top goal line, 1 = bottom goal line. The y of a slot is the shirt's
 * anchor, the same point the wizard translates a slot <g> to (the shirt art
 * runs from -82 to +45 around it and the two name lines sit below, to about
 * +108, at scale 1). Slots are ordered like the wizard: GK first, then each
 * row from the back, left to right.
 *
 * Shirt scale: s = 1 is the wizard's current shirt (104 x 127 in the 900-wide
 * print, names fitted to 190). Each format has a cap (fewer players = bigger
 * shirts) and each formation gets the largest scale up to that cap that keeps
 * shirt + two name lines clear of every neighbour (190s wide, 190s tall + 6px).
 * Rows of 5 across get their own smaller scale, like the wizard does today.
 */
(function (root) {
  'use strict';

  /* ======================================================================
     Official dimensions (metres). Sources:
     [IFAB]  IFAB Laws of the Game 2025/26, Law 1 The Field of Play.
     [FA38]  The FA Handbook 2025-26, Rules of the Association s.38
             "Goalpost and pitch sizes and line marking" (pitch sizes by age group).
             thefa.com/-/media/files/thefaportal/governance-docs/rules-of-the-association/2025-26/38---goalpost-and-pitch-sizes-and-line-marking.ashx
     [FAPD]  The FA Guide to Pitch Dimensions (metric), recommended pitch sizes:
             5v5 37 x 27 m, 7v7 55 x 37 m, 9v9 73 x 46 m, adult 100 x 64 m.
     [FAYF]  FA youth formats (Mini-Soccer / 9v9 laws), area markings:
             5v5  penalty mark 6 m, centre circle 2.75 m (3 yd) "if included".
             7v7  penalty area 18 x 10 yd (16.46 x 9.14 m), penalty mark 8 yd (7.32 m).
             9v9  penalty area 32 x 13 yd (29.26 x 11.88 m), goal area 14 x 4 yd
                  (12.80 x 3.66 m), penalty mark 9 yd (8.23 m).
                  Centre circle: FA 9v9 guidance gives 7 yd (6.40 m); some secondary
                  sources quote 10 yd. 7 yd used here (keeps the 11v11 ratio).
     [FASS]  The FA Small-Sided Football Laws of the Game, Law 1 (5 and 6-a-side):
             length 25-50 m, width 16-35 m; penalty area = semicircle of 6 m radius
             from the centre of each goal line; centre circle 3 m radius; penalty
             mark 6 m. thefa.com/football-rules-governance/lawsandrules/laws/football-5-5/law-1---the-playing-area
     [FAWF]  The FA Walking Football Laws of the Game (Feb 2024), Law 1: same
             playing area as [FASS] (25-50 x 16-35 m, 6 m semicircle, 3 m circle).
     [FIFAF] FIFA Futsal Laws of the Game 2023/24, Law 1 The Pitch:
             international 38-42 x 20-25 m; goal 3 x 2 m; penalty area = two
             quarter circles of 6 m radius centred on the outside of each goalpost,
             joined by a 3.16 m line parallel to the goal line; penalty mark 6 m;
             second penalty mark 10 m; centre circle 3 m radius; corner arc 0.25 m;
             substitution zones 5 m long in front of the benches, starting 5 m from
             the halfway line, marked by 80 cm lines (40 cm in, 40 cm out).
     ====================================================================== */
  var YD = 0.9144;
  var PITCH_TYPES = {
    // 11v11: IFAB recommended 105 x 68 (FA adult recommendation 100 x 64 [FAPD]).
    full: {
      label: '11-a-side', length: 105, width: 68,
      box: { w: 40.32, d: 16.5 },          // [IFAB]
      goalArea: { w: 18.32, d: 5.5 },      // [IFAB]
      penMark: 11, arcR: 9.15,             // [IFAB] penalty arc = 9.15 m from the mark
      circleR: 9.15, corner: 1             // [IFAB]
    },
    // 9v9: FA U11/U12, 80 x 50 yd max [FA38], 73 x 46 m recommended [FAPD].
    nine: {
      label: '9-a-side', length: 73, width: 46,
      box: { w: 32 * YD, d: 13 * YD },     // [FAYF]
      goalArea: { w: 14 * YD, d: 4 * YD }, // [FAYF]
      penMark: 9 * YD, arcR: 7 * YD,
      circleR: 7 * YD, corner: 0.75
    },
    // 7v7: FA U9/U10, 60 x 40 yd [FA38], 55 x 37 m [FAPD]. No goal area in 7v7.
    seven: {
      label: '7-a-side', length: 55, width: 37,
      box: { w: 18 * YD, d: 10 * YD },     // [FAYF]
      goalArea: null,
      penMark: 8 * YD, arcR: 0,
      circleR: 5 * YD, corner: 0.6         // FA mini-soccer: circle optional. 5 yd drawn for balance.
    },
    // Adult 5/6-a-side and walking football: within 25-50 x 16-35 m [FASS][FAWF].
    // 40 x 25 m is a typical commercial small-sided pitch and sits mid-range.
    small: {
      label: 'Small-sided', length: 40, width: 25,
      dRadius: 6,                          // [FASS] 6 m semicircle from goal-line centre
      penMark: 6, circleR: 3, corner: 0     // [FASS]
    },
    // Futsal: 40 x 20 m, inside the FIFA international range [FIFAF].
    futsal: {
      label: 'Futsal', length: 40, width: 20,
      goalW: 3, postW: 0.08,               // [FIFAF] 3 m goal, 8 cm posts
      dRadius: 6, dLine: 3.16,             // [FIFAF]
      penMark: 6, secondMark: 10,          // [FIFAF]
      circleR: 3, corner: 0.25,            // [FIFAF]
      subZone: { from: 5, len: 5, tick: 0.8 } // [FIFAF]
    }
  };

  /* ======================================================================
     Geometry: the original master pitch
     ----------------------------------------------------------------------
     Traced, not redrawn. Every YourXI InDesign master (all 16 PDFs in
     YourXI/*INDD MASTER/, e.g. NEW - 25CUP.pdf, A1 page 1683.78 x 2383.94 pt)
     carries the same pitch: one grouped object, stroke #DADAD9 (ICC RGB
     .855/.855/.854) with the group at 40% opacity, so it prints as #F0F0F0
     on white. Mitre joins, butt caps. Three stroke weights by depth:
       11.406 pt  outline
       10.932 pt  near penalty box, near six-yard box, near arc
        8.555 pt  halfway line, centre ellipse, far boxes, far arc
     The outline is a trapezoid 1478.007 pt wide at the near goal line and
     1052.265 pt at the far one (71.2%), 1538.233 pt tall. It is not a true
     perspective: every lengthwise line moves inward by the same 0.13839 pt
     per pt of height (touchlines and box sides are parallel pairs), circles
     are plain ellipses, and the boxes are drawn bigger than scale so they
     read. That hand-drawn language is the feature, so 11-a-side uses his
     path data verbatim and the other formats are built with the same rules.
     On the A1 master the outline sits at x 102.89-1580.89, y 493.93-2032.17
     (top 20.7%, bottom 85.2% of the page); title baselines at 181 / 299 / 394,
     footer text 2084-2306.
     Coordinates below are pt relative to the outline's top-left corner.
     Note: his centre ellipse and both arcs sit 6.1 pt left of the pitch
     centre line (732.9 vs 739.0). Kept, so it matches the prints; set
     CENTRE_FIX = true to centre them.
     ====================================================================== */
  var M_W = 1478.007, M_H = 1538.233, M_CX = M_W / 2;
  var M_SLOPE = 212.871 / 1538.233;               // inward shift per unit of height
  var M_HALF = 710.744;                           // halfway line y
  var M_WEIGHTS = { outline: 11.406, near: 10.932, far: 8.555 };
  var CENTRE_FIX = true;
  var MASTER_11 = {
    outline: 'M1478.007 1538.233 L0 1538.233 L212.871 0 L1265.136 0 L1478.007 1538.233 Z',
    near: 'M220.599 1538.233 L270.7 1176.081 L1207.334 1176.081 L1257.408 1538.233' +
      ' M1043.111 1538.233 L434.952 1538.233 L461.729 1344.582 L1016.278 1344.582 L1043.111 1538.233 Z',
    nearArc: 'M459.742 1180.489 C518.006 1113.984 619.092 1069.996 734.037 1069.996 C846.85 1069.996 946.312 1112.366 1005.045 1176.81',
    far: 'M114.517 710.744 L1363.506 710.744' +
      ' M378.134 295.788 L1099.889 295.788 L1058.956 -0.004 L419.067 -0.004 L378.134 295.788 Z' +
      ' M539.86 163.231 L938.164 163.231 L915.575 -0.004 L562.449 -0.004 L539.86 163.231 Z',
    circle: 'M955.797 710.098 C955.797 801.351 856.003 875.327 732.901 875.327 C609.8 875.327 510.006 801.351 510.006 710.098 C510.006 618.845 609.8 544.869 732.901 544.869 C856.003 544.869 955.797 618.845 955.797 710.098 Z',
    farArc: 'M887.99 295.794 C855.047 333.397 797.893 358.268 732.901 358.268 C669.116 358.268 612.88 334.311 579.672 297.874'
  };
  // Measured from his 11-a-side, used to build the other formats in the same language:
  //   box width at the goal line  = (real width / pitch width) ^ 0.676 of the outline width there
  //     (penalty box .593 -> .7015, six-yard .269 -> .4115 near; exact fit)
  //   box depth (near)            = 0.676 x (real depth / pitch length) ^ 0.57 of M_H
  //     (penalty box .157 -> .2354, six-yard .052 -> .1259; exact fit)
  //   far end: widths x 0.84, depths x 0.83 (his far boxes: .867/.816 and .817/.843)
  //   centre ellipse rx = 1.326 x (radius / pitch width) x halfway-line length, ry = 0.741 rx
  //   arcs reuse his two arc curves, scaled to the box mouth (near .582, far .482 of it)
  var LANG = { wPow: 0.676, dK: 0.676, dPow: 0.57, farW: 0.84, farD: 0.83, circK: 1.326, circRatio: 0.7412 };

  var REF_W = 780, REF_H = 758;   // wizard pitch box on the 900 x 1200 print (bottom goal line at y 1020)

  function halfAt(y) { return M_CX - M_SLOPE * (M_H - y); }            // half-width of the outline at y
  function depthAlong(t) { // t = 0 near goal line .. 1 far goal line -> y (fits his halfway line exactly)
    var a = 1 + 4 * ((M_H - M_HALF) / M_H - 0.5);                       // 1.1521
    return M_H - M_H * (a * t + (1 - a) * t * t);
  }

  // fit the master frame into the box: uniform scale, bottom-centre anchored
  function frame(opts) {
    var W = opts.width || REF_W, H = opts.height || REF_H;
    var s = Math.min(W / M_W, H / M_H);
    return { W: W, H: H, s: s, ox: (W - M_W * s) / 2, oy: H - M_H * s };
  }

  function pitchGeometry(formatId, opts) {
    opts = opts || {};
    var fmt = getFormat(formatId), P = PITCH_TYPES[fmt.pitch], F = frame(opts);
    var tx = function (x, y) { return [F.ox + x * F.s, F.oy + y * F.s]; };
    function project(u, v) { // metres: u across from the left touchline, v from the FAR (top) goal line
      var y = depthAlong(1 - v / P.length);
      return tx(M_CX + (u / P.width - 0.5) * 2 * halfAt(y), y);
    }
    return {
      width: F.W, height: F.H, scale: F.s, dims: P, project: project,
      corners: [tx(212.871, 0), tx(1265.136, 0), tx(M_W, M_H), tx(0, M_H)],
      widthAt: function (y) { return 2 * halfAt((y - F.oy) / F.s) * F.s; },
      leftAt: function (y) { var my = (y - F.oy) / F.s; return F.ox + (M_CX - halfAt(my)) * F.s; }
    };
  }

  // ---- derived markings, in master pt, same rules as his drawing ----
  function f3p(n) { return Math.round(n * 1000) / 1000; }
  function boxPath(P, realW, realD, far, closeTop) {
    var wFrac = Math.pow(realW / P.width, LANG.wPow);
    var dFrac = LANG.dK * Math.pow(realD / P.length, LANG.dPow);
    var g = far ? 0 : M_H, gHalf = halfAt(g);
    var half = wFrac * gHalf * (far ? LANG.farW : 1);
    var d = dFrac * M_H * (far ? LANG.farD : 1), y2 = far ? d : M_H - d;
    var half2 = half + M_SLOPE * (y2 - g);                                // same slant as his box sides
    var pts = [[M_CX - half, g], [M_CX - half2, y2], [M_CX + half2, y2], [M_CX + half, g]];
    return { d: 'M' + pts.map(function (p) { return f3p(p[0]) + ' ' + f3p(p[1]); }).join(' L') + (closeTop ? ' Z' : ''), half2: half2, y2: y2 };
  }
  function ellipseD(cx, cy, rx, ry) {
    var k = 0.5523, X = function (n) { return f3p(n); };
    return 'M' + X(cx + rx) + ' ' + X(cy) +
      ' C' + X(cx + rx) + ' ' + X(cy + ry * k) + ' ' + X(cx + rx * k) + ' ' + X(cy + ry) + ' ' + X(cx) + ' ' + X(cy + ry) +
      ' C' + X(cx - rx * k) + ' ' + X(cy + ry) + ' ' + X(cx - rx) + ' ' + X(cy + ry * k) + ' ' + X(cx - rx) + ' ' + X(cy) +
      ' C' + X(cx - rx) + ' ' + X(cy - ry * k) + ' ' + X(cx - rx * k) + ' ' + X(cy - ry) + ' ' + X(cx) + ' ' + X(cy - ry) +
      ' C' + X(cx + rx * k) + ' ' + X(cy - ry) + ' ' + X(cx + rx) + ' ' + X(cy - ry * k) + ' ' + X(cx + rx) + ' ' + X(cy) + ' Z';
  }
  // his arcs, normalised to their chord (x 0..1 across the chord, y in chord widths, + = into the pitch)
  var ARC_NEAR = [[0, -0.0081], [0.1068, 0.1139], [0.2922, 0.1945], [0.503, 0.1945], [0.7099, 0.1945], [0.8923, 0.1168], [1, -0.0013]];
  var ARC_FAR = [[0, 0.0068], [0.1077, 0.1249], [0.2901, 0.2026], [0.497, 0.2026], [0.7078, 0.2026], [0.8932, 0.122], [1, 0]];
  function arcD(chordCx, y, width, far) { // y = the box line the arc sits on
    var A = far ? ARC_FAR : ARC_NEAR, x0 = chordCx - width / 2, dir = far ? 1 : -1;
    var p = function (i) { return f3p(x0 + A[i][0] * width) + ' ' + f3p(y + dir * A[i][1] * width); };
    return 'M' + p(0) + ' C' + p(1) + ' ' + p(2) + ' ' + p(3) + ' C' + p(4) + ' ' + p(5) + ' ' + p(6);
  }
  // semicircle D on the goal line (small-sided), or futsal's quarter circles + straight
  function goalAreaD(P, totalW, straightW, depth, far) {
    var wFrac = Math.pow(totalW / P.width, LANG.wPow);
    var g = far ? 0 : M_H, gHalf = halfAt(g);
    var half = wFrac * gHalf * (far ? LANG.farW : 1);
    var d = LANG.dK * Math.pow(depth / P.length, LANG.dPow) * M_H * (far ? LANG.farD : 1);
    var dir = far ? 1 : -1, s = straightW / totalW, k = 0.5523;
    var x0 = M_CX - half, x1 = M_CX - half * s, x2 = M_CX + half * s, x3 = M_CX + half, yT = g + dir * d;
    var rx = x1 - x0, X = f3p;
    return 'M' + X(x0) + ' ' + X(g) +
      ' C' + X(x0) + ' ' + X(g + dir * d * k) + ' ' + X(x1 - rx * k) + ' ' + X(yT) + ' ' + X(x1) + ' ' + X(yT) +
      (x2 > x1 ? ' L' + X(x2) + ' ' + X(yT) : '') +
      ' C' + X(x2 + rx * k) + ' ' + X(yT) + ' ' + X(x3) + ' ' + X(g + dir * d * k) + ' ' + X(x3) + ' ' + X(g);
  }

  // returns {outline, near, far} path data in master pt for a pitch type
  function masterMarkings(type, opts) {
    if (type === 'full') {
      var m = MASTER_11, shift = CENTRE_FIX ? ' transform="translate(6.1 0)"' : '';
      return { outline: m.outline, near: m.near, nearExtra: [[m.nearArc, shift]], far: m.far, farExtra: [[m.circle + ' ' + m.farArc, shift]] };
    }
    var P = PITCH_TYPES[type], near = [], far = ['M' + f3p(M_CX - halfAt(M_HALF)) + ' ' + M_HALF + ' L' + f3p(M_CX + halfAt(M_HALF)) + ' ' + M_HALF];
    if (P.circleR) {
      var rx = LANG.circK * (P.circleR / P.width) * 2 * halfAt(M_HALF);
      far.push(ellipseD(M_CX, M_HALF, rx, rx * LANG.circRatio));
    }
    if (P.box) {
      var nb = boxPath(P, P.box.w, P.box.d, false), fb = boxPath(P, P.box.w, P.box.d, true);
      near.push(nb.d); far.push(fb.d);
      if (P.goalArea) { near.push(boxPath(P, P.goalArea.w, P.goalArea.d, false, true).d); far.push(boxPath(P, P.goalArea.w, P.goalArea.d, true, true).d); }
      if (P.arcR) { near.push(arcD(M_CX, nb.y2, 0.582 * 2 * nb.half2, false)); far.push(arcD(M_CX, fb.y2, 0.482 * 2 * fb.half2, true)); }
    }
    if (type === 'small') {
      near.push(goalAreaD(P, 2 * P.dRadius, 0, P.dRadius, false));
      far.push(goalAreaD(P, 2 * P.dRadius, 0, P.dRadius, true));
    }
    if (type === 'futsal') {
      var tw = 2 * (P.dRadius + P.goalW / 2 + P.postW);
      near.push(goalAreaD(P, tw, P.dLine, P.dRadius, false));
      far.push(goalAreaD(P, tw, P.dLine, P.dRadius, true));
    }
    return { outline: MASTER_11.outline, near: near.join(' '), far: far.join(' ') };
  }

  // marks on the pitch (penalty spots, futsal second marks, sub-zone ticks), master pt
  function masterSpots(type, opts) {
    var P = PITCH_TYPES[type], dots = [], ticks = [];
    var yOf = function (fromNear) { return depthAlong(fromNear / P.length); };
    if (opts.spots) {
      dots.push([M_CX, M_HALF]);
      var depthNear = function (m) { return M_H - LANG.dK * Math.pow(m / P.length, LANG.dPow) * M_H; };
      var depthFar = function (m) { return LANG.dK * Math.pow(m / P.length, LANG.dPow) * M_H * LANG.farD; };
      [P.penMark, P.secondMark].forEach(function (m) { if (m) { dots.push([M_CX, depthNear(m)], [M_CX, depthFar(m)]); } });
    }
    if (type === 'futsal' && opts.subZones) {
      var z = P.subZone, h = P.length / 2;
      [h - z.from, h - z.from - z.len, h + z.from, h + z.from + z.len].forEach(function (v) {
        var y = yOf(v), x = M_CX - halfAt(y);
        ticks.push('M' + f3p(x - 18) + ' ' + f3p(y) + ' L' + f3p(x + 18) + ' ' + f3p(y));
      });
    }
    return { dots: dots, ticks: ticks.join(' ') };
  }

  /* ======================================================================
     Styles
     ====================================================================== */
  function hexToRgb(h) {
    h = String(h || '#000').replace('#', '');
    if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(r) {
    return '#' + r.map(function (v) { v = Math.max(0, Math.min(255, Math.round(v))); return (v < 16 ? '0' : '') + v.toString(16); }).join('').toUpperCase();
  }
  function mix(a, b, t) { var A = hexToRgb(a), B = hexToRgb(b); return rgbToHex([0, 1, 2].map(function (i) { return A[i] + (B[i] - A[i]) * t; })); }
  function lum(c) {
    var r = hexToRgb(c).map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * r[0] + 0.7152 * r[1] + 0.0722 * r[2];
  }
  function contrast(a, b) { var A = lum(a), B = lum(b); return (Math.max(A, B) + 0.05) / (Math.min(A, B) + 0.05); }
  // walk from `from` towards `to` until the contrast against `against` reaches `target`
  function towardContrast(from, to, against, target) {
    for (var t = 0; t <= 1.0001; t += 0.01) { var c = mix(from, to, t); if (contrast(c, against) >= target) return c; }
    return to;
  }

  // Style fields: paper (page background), line, lineWidth (outline weight in px at the wizard
  // size; 5.621 = his 11.406 pt. The near/far tiers scale with it),
  // textPrimary (names, title), textSecondary (message lines), markColour (YOURXI mark,
  // captain badge), halo (paint a 6px stroke of this behind names, paint-order="stroke",
  // so pitch lines never run through a name), shadowOnDark (true = drop the grey shirt
  // shadow, it reads as a smudge on dark paper).
  // classic line #F0F0F0 on white = 1.14:1, a whisper (his exact value). Dark styles sit at about 1.6-2.5:1
  // so lines survive dot gain on dark stock without fighting the names.
  var PITCH_STYLES = {
    classic: {
      id: 'classic', label: 'Classic',
      paper: '#FFFFFF', line: '#F0F0F0', lineWidth: 5.621,   // his exact line: #DADAD9 at 40% = #F0F0F0, outline 11.406 pt on A1
      textPrimary: '#111111', textSecondary: '#62625F', markColour: '#111111',
      halo: '#FFFFFF', shadowOnDark: false
    },
    night: {
      id: 'night', label: 'Night',
      paper: '#141414', line: '#3A3936', lineWidth: 5.621,
      textPrimary: '#F5F3EE', textSecondary: '#A9A7A1', markColour: '#F5F3EE',
      halo: '#141414', shadowOnDark: true
    },
    grass: {
      id: 'grass', label: 'Grass',
      paper: '#2F4A36', line: '#A9BBA7', lineWidth: 5.621,   // soft white: full white fought the names
      stripes: ['#36553D', '#3C5C43'],   // mown bands inside the pitch only
      textPrimary: '#FFFFFF', textSecondary: '#CFDACB', markColour: '#FFFFFF',
      halo: '#39583F', shadowOnDark: true
    },
    clubTint: function (colour) {
      colour = colour || '#1F3A93';
      var paper = mix(colour, '#FFFFFF', 0.92);
      // darker tonal lines: same hue, pushed until they read about like classic
      var line = towardContrast(paper, mix(colour, '#000000', 0.15), paper, 1.32);
      var mark = towardContrast(mix(colour, '#000000', 0.35), '#111111', paper, 7);
      return {
        id: 'clubTint', label: 'Club tint', tint: colour,
        paper: paper, line: line, lineWidth: 5.621,
        textPrimary: towardContrast(mix(colour, '#000000', 0.82), '#111111', paper, 12),
        textSecondary: towardContrast(mix(colour, '#000000', 0.45), '#111111', paper, 4.6),
        markColour: mark, halo: paper, shadowOnDark: false
      };
    },
    chalkboard: {
      id: 'chalkboard', label: 'Chalkboard',
      paper: '#2A302E', line: '#E3E1D8', lineWidth: 5.621, lineOpacity: 0.5,
      texture: 'chalk',
      textPrimary: '#F2F0E8', textSecondary: '#AEB3AD', markColour: '#F2F0E8',
      halo: '#2A302E', shadowOnDark: true
    }
  };

  function getStyle(styleId, colour) {
    if (styleId && typeof styleId === 'object') return styleId;
    var s = PITCH_STYLES[styleId || 'classic'] || PITCH_STYLES.classic;
    return typeof s === 'function' ? s(colour) : s;
  }

  /* ======================================================================
     Rendering
     ====================================================================== */
  var uid = 0;
  function f1(n) { return Math.round(n * 10) / 10; }
  function pathD(pts, close) {
    return pts.map(function (p, i) { return (i ? 'L' : 'M') + f1(p[0]) + ' ' + f1(p[1]); }).join(' ') + (close ? ' Z' : '');
  }

  function paperSVG(styleId, opts) {
    opts = opts || {};
    var st = getStyle(styleId, opts.colour), W = opts.width || 900, H = opts.height || 1200;
    var out = '<rect width="' + W + '" height="' + H + '" fill="' + st.paper + '"/>';
    if (st.texture === 'chalk') { // faint dust and old wipes on the slate, kept very low so it prints clean
      var id = 'yxp' + (++uid);
      out += '<filter id="' + id + 'd" x="0" y="0" width="100%" height="100%">' +
        '<feTurbulence type="fractalNoise" baseFrequency="0.004 0.012" numOctaves="3" seed="11"/>' +
        '<feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.9 -0.36"/></filter>' +
        '<rect width="' + W + '" height="' + H + '" filter="url(#' + id + 'd)" opacity=".10"/>';
    }
    return '<g class="yx-paper">' + out + '</g>';
  }

  function pitchSVG(formatId, styleId, opts) {
    opts = opts || {};
    var st = getStyle(styleId, opts.colour);
    var fmt = getFormat(formatId), type = fmt.pitch, P = PITCH_TYPES[type];
    var F = frame(opts);
    var id = opts.id || ('yxp' + (++uid));
    var lw = (opts.lineWidth || st.lineWidth) / 5.621;          // style weight relative to his (5.621 px outline at the wizard size)
    var defs = '', under = '', filt = '';
    var mk = masterMarkings(type, opts), sp = masterSpots(type, opts);

    if (st.stripes) { // mown bands inside the outline, spaced in his depth
      var n = type === 'full' ? 14 : type === 'nine' ? 12 : 10, bands = '';
      for (var i = 0; i < n; i++) {
        var y0 = depthAlong(i / n), y1 = depthAlong((i + 1) / n), a0 = halfAt(y0), a1 = halfAt(y1);
        bands += '<path d="M' + f3p(M_CX - a0) + ' ' + f3p(y0) + ' L' + f3p(M_CX + a0) + ' ' + f3p(y0) + ' L' + f3p(M_CX + a1) + ' ' + f3p(y1) + ' L' + f3p(M_CX - a1) + ' ' + f3p(y1) + ' Z" fill="' + st.stripes[i % 2] + '"/>';
      }
      under = '<g class="yx-turf">' + bands + '</g>';
    }
    if (st.texture === 'chalk') {
      // slight wobble + chalk grain, in master pt (about 1.3 px at print size) so lines stay crisp
      defs += '<filter id="' + id + 'c" x="-2%" y="-2%" width="104%" height="104%">' +
        '<feTurbulence type="fractalNoise" baseFrequency="0.022" numOctaves="2" seed="4" result="w"/>' +
        '<feDisplacementMap in="SourceGraphic" in2="w" scale="5" xChannelSelector="R" yChannelSelector="G" result="d"/>' +
        '<feTurbulence type="fractalNoise" baseFrequency="0.45" numOctaves="1" seed="9" result="g"/>' +
        '<feColorMatrix in="g" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.4 2.05" result="m"/>' +
        '<feComposite in="d" in2="m" operator="in"/></filter>';
      filt = ' filter="url(#' + id + 'c)"';
    }
    var W = M_WEIGHTS, sw = function (w) { return f3p(w * lw); };
    var extra = function (list, w) { return (list || []).map(function (e) { return '<path d="' + e[0] + '" stroke-width="' + sw(w) + '"' + e[1] + '/>'; }).join(''); };
    var lines =
      '<path d="' + mk.outline + '" stroke-width="' + sw(W.outline) + '"/>' +
      (mk.near ? '<path d="' + mk.near + '" stroke-width="' + sw(W.near) + '"/>' : '') + extra(mk.nearExtra, W.near) +
      (mk.far ? '<path d="' + mk.far + '" stroke-width="' + sw(W.far) + '"/>' : '') + extra(mk.farExtra, W.far) +
      (sp.ticks ? '<path d="' + sp.ticks + '" stroke-width="' + sw(W.far) + '"/>' : '') +
      sp.dots.map(function (p) { return '<circle cx="' + f3p(p[0]) + '" cy="' + f3p(p[1]) + '" r="' + sw(W.near * 0.9) + '" fill="' + st.line + '" stroke="none"/>'; }).join('');
    // his lines are one group at 40% opacity: overlaps never darken. A solid colour (classic #F0F0F0)
    // gives the identical result on flat paper; lineOpacity is applied to the whole group the same way.
    var op = st.lineOpacity ? ' opacity="' + st.lineOpacity + '"' : '';
    return '<g class="yx-pitch" data-format="' + fmt.id + '" data-style="' + st.id + '">' +
      (defs ? '<defs>' + defs + '</defs>' : '') +
      '<g transform="translate(' + f3p(F.ox) + ' ' + f3p(F.oy) + ') scale(' + (Math.round(F.s * 1e6) / 1e6) + ')">' + under +
      '<g fill="none" stroke="' + st.line + '" stroke-linejoin="miter" stroke-miterlimit="10" stroke-linecap="butt"' + op + filt + '>' + lines + '</g></g></g>';
  }

  /* ======================================================================
     Formats and formations
     Rows are listed back to front (after the GK). A row is either a list of
     fine roles (spread automatically, like the wizard; used for 11 and 9)
     or a list of D(fine, x, r) placed by hand. r = depth, 0 = GK line,
     1 = front line. The small formats are placed by hand and staggered
     (wide players sit off the centre column) so bigger shirts still clear
     each other: a strict row-per-line layout caps every 4-line shape at
     about 1.08 whatever the player count.
     ====================================================================== */
  var COARSE = { GK: 'GK', CB: 'DF', FB: 'DF', WB: 'DF', DM: 'MF', CM: 'MF', AM: 'MF', W: 'MF', ST: 'FW' };

  // layout constants, in reference-box pixels at shirt scale s
  var SHIRT_UP = 82, SHIRT_DOWN = 108, FOOT_W = 190, GAP = 6;
  var TOP_ANCHOR = 0.066, GK_ANCHOR = 0.926;    // wizard's rows at s = 1

  function spread(count, s, t) { // horizontal span (px) for an automatic row, mirrors the wizard
    if (count < 2) return 0;
    var cap = count >= 5 ? 700 : 640 + t * 60;
    return Math.min(cap, (count - 1) * 205 * Math.max(1, s));
  }

  function build(formatCap, rows) {
    // returns {scale, slots} with the biggest uniform scale <= cap that fits, row-of-5 scale reduced separately
    for (var s = formatCap; s >= 0.6; s = Math.round((s - 0.01) * 100) / 100) {
      var res = place(s, rows);
      if (!collides(res)) return { scale: s, slots: res };
    }
    return { scale: 0.6, slots: place(0.6, rows) };
  }

  function place(s, rows) {
    var up = Math.max(0, s - 1);
    var top = TOP_ANCHOR + (SHIRT_UP * up) / REF_H;     // bigger shirts: keep the top shirt edge where it is today
    var gk = GK_ANCHOR - (SHIRT_DOWN * up) / REF_H;     // and the GK's surname line too
    var autoRows = rows.length;
    var yOf = function (r) { return gk - (gk - top) * r; };   // r: 0 = GK line, 1 = front line
    var out = [{ x: 0.5, y: f3(gk), role: 'GK', fine: 'GK', s: 1 }];
    rows.forEach(function (row, ri) {
      var auto = typeof row[0] === 'string';
      var count = row.length;
      // a row of 5 across cannot hold full-size names, shrink just that row (wizard does .86)
      var rowS = auto && count >= 5 ? Math.min(1, (700 / (count - 1) - GAP - 4) / (FOOT_W * s)) : 1;
      row.forEach(function (it, c) {
        var y = yOf(auto ? (ri + 1) / autoRows : it.r), x;
        if (auto) {
          var span = spread(count, s * rowS, y);
          x = count === 1 ? 0.5 : 0.5 + (c / (count - 1) - 0.5) * span / REF_W;
        } else x = it.x;
        var fine = auto ? it : it.fine;
        out.push({ x: f3(x), y: f3(y), role: COARSE[fine], fine: fine, s: f3(rowS) });
      });
    });
    out.forEach(function (o) { o._s = s * o.s; });
    return out;
  }
  function f3(n) { return Math.round(n * 1000) / 1000; }

  function collides(slots) {
    for (var i = 0; i < slots.length; i++) {
      var a = slots[i];
      if (a.x * REF_W - (FOOT_W / 2) * a._s < -50 || a.x * REF_W + (FOOT_W / 2) * a._s > REF_W + 50) return true;
      for (var j = i + 1; j < slots.length; j++) {
        var b = slots[j];
        var dx = Math.abs(a.x - b.x) * REF_W, need = (FOOT_W / 2) * (a._s + b._s) + 4;
        if (dx >= need) continue;
        var ay0 = a.y * REF_H - SHIRT_UP * a._s, ay1 = a.y * REF_H + SHIRT_DOWN * a._s;
        var by0 = b.y * REF_H - SHIRT_UP * b._s, by1 = b.y * REF_H + SHIRT_DOWN * b._s;
        if (ay0 < by1 + GAP && by0 < ay1 + GAP) return true;
      }
    }
    return false;
  }

  function formation(id, label, cap, rows, extra) {
    var b = build(cap, rows);
    var f = { id: id, label: label || id, scale: b.scale, slots: b.slots.map(function (o) {
      var r = { x: o.x, y: o.y, role: o.role, fine: o.fine };
      if (o.s !== 1) r.s = o.s;            // per-slot multiplier on top of formation scale (rows of 5)
      return r;
    }) };
    if (extra) for (var k in extra) f[k] = extra[k];
    return f;
  }

  var D = function (fine, x, r) { return { fine: fine, x: x, r: r }; };

  function eleven(cap) {
    return [
      formation('4-4-2', '4-4-2', cap, [['FB', 'CB', 'CB', 'FB'], ['W', 'CM', 'CM', 'W'], ['ST', 'ST']]),
      formation('4-3-3', '4-3-3', cap, [['FB', 'CB', 'CB', 'FB'], ['CM', 'CM', 'CM'], ['W', 'ST', 'W']]),
      formation('3-5-2', '3-5-2', cap, [['CB', 'CB', 'CB'], ['WB', 'CM', 'CM', 'CM', 'WB'], ['ST', 'ST']]),
      formation('4-2-3-1', '4-2-3-1', cap, [['FB', 'CB', 'CB', 'FB'], ['DM', 'DM'], ['W', 'AM', 'W'], ['ST']]),
      formation('3-4-3', '3-4-3', cap, [['CB', 'CB', 'CB'], ['WB', 'CM', 'CM', 'WB'], ['W', 'ST', 'W']]),
      formation('5-3-2', '5-3-2', cap, [['WB', 'CB', 'CB', 'CB', 'WB'], ['CM', 'CM', 'CM'], ['ST', 'ST']])
    ];
  }
  function nine(cap) {
    return [
      formation('3-3-2', '3-3-2', cap, [['CB', 'CB', 'CB'], ['W', 'CM', 'W'], ['ST', 'ST']]),
      formation('3-2-3', '3-2-3', cap, [['CB', 'CB', 'CB'], ['CM', 'CM'], ['W', 'ST', 'W']]),
      formation('2-4-2', '2-4-2', cap, [['CB', 'CB'], ['W', 'CM', 'CM', 'W'], ['ST', 'ST']])
    ];
  }
  function seven(cap) {
    return [
      formation('2-3-1', '2-3-1', cap, [[D('CB', 0.21, 0.27), D('CB', 0.79, 0.27)], [D('W', 0.14, 0.64), D('CM', 0.5, 0.6), D('W', 0.86, 0.64)], [D('ST', 0.5, 1)]]),
      formation('3-2-1', '3-2-1', cap, [[D('FB', 0.17, 0.3), D('CB', 0.5, 0.37), D('FB', 0.83, 0.3)], [D('CM', 0.21, 0.73), D('CM', 0.79, 0.73)], [D('ST', 0.5, 1)]]),
      // diamond midfield: holder just in front of the back two, wide pair level with the gap
      formation('2-1-2-1', '2-1-2-1', cap, [[D('CB', 0.21, 0.27), D('CB', 0.79, 0.27)], [D('DM', 0.5, 0.45)], [D('W', 0.14, 0.7), D('W', 0.86, 0.7)], [D('ST', 0.5, 1)]]),
      formation('3-1-2', '3-1-2', cap, [[D('FB', 0.17, 0.3), D('CB', 0.5, 0.37), D('FB', 0.83, 0.3)], [D('CM', 0.5, 0.74)], [D('ST', 0.21, 1), D('ST', 0.79, 1)]])
    ];
  }
  function six(cap) {
    return [
      formation('2-2-1', '2-2-1', cap, [[D('CB', 0.2, 0.32), D('CB', 0.8, 0.32)], [D('CM', 0.19, 0.72), D('CM', 0.81, 0.72)], [D('ST', 0.5, 1)]]),
      // one centre column of four (GK, CB, CM, ST) caps this shape lower than the others
      formation('3-1-1', '3-1-1', cap, [[D('FB', 0.17, 0.3), D('CB', 0.5, 0.34), D('FB', 0.83, 0.3)], [D('CM', 0.5, 0.67)], [D('ST', 0.5, 1)]]),
      formation('2-1-2', '2-1-2', cap, [[D('CB', 0.2, 0.28), D('CB', 0.8, 0.28)], [D('CM', 0.5, 0.55)], [D('ST', 0.3, 1), D('ST', 0.7, 1)]])
    ];
  }
  function five(cap, futsal) {
    var box = formation('2-2', futsal ? '2-2 box' : '2-2', cap, [[D('CB', 0.29, 0.45), D('CB', 0.71, 0.45)], [D('ST', 0.29, 1), D('ST', 0.71, 1)]]);
    var diamond = formation('1-2-1', '1-2-1 diamond', cap, [[D('CB', 0.5, 0.42)], [D('W', 0.15, 0.7), D('W', 0.85, 0.7)], [D('ST', 0.5, 1)]]);
    if (!futsal) return [box, diamond,
      formation('1-1-2', '1-1-2', cap, [[D('CB', 0.5, 0.41)], [D('CM', 0.5, 0.82)], [D('ST', 0.19, 1), D('ST', 0.81, 1)]])];
    return [
      diamond, box,
      formation('3-1', '3-1', cap, [[D('FB', 0.18, 0.35), D('CB', 0.5, 0.45), D('FB', 0.82, 0.35)], [D('ST', 0.5, 1)]]),
      // 4-0, the rotation system: four across the middle, inner pair pushed on so names clear
      formation('4-0', '4-0', cap, [[D('W', 0.15, 0.5), D('CM', 0.34, 0.95), D('CM', 0.66, 0.95), D('W', 0.85, 0.5)]])
    ];
  }

  function fmt(id, label, players, maxSubs, pitch, cap, formations, extra) {
    var o = { id: id, label: label, players: players, maxSubs: maxSubs, pitch: pitch, shirtScale: cap, formations: formations, defaultFormation: formations[0].id };
    if (extra) for (var k in extra) o[k] = extra[k];
    return o;
  }

  var FORMATS = [
    fmt('eleven', '11-a-side', 11, 5, 'full', 1.0, eleven(1.0)),
    fmt('nine', '9-a-side', 9, 5, 'nine', 1.08, nine(1.08)),
    fmt('seven', '7-a-side', 7, 5, 'seven', 1.15, seven(1.15)),
    fmt('six', '6-a-side', 6, 4, 'small', 1.2, six(1.2)),
    fmt('five', '5-a-side', 5, 5, 'small', 1.25, five(1.25)),
    fmt('futsal', 'Futsal', 5, 5, 'futsal', 1.25, five(1.25, true)),
    fmt('walking5', 'Walking football 5-a-side', 5, 5, 'small', 1.25, five(1.25), { walking: true }),
    fmt('walking6', 'Walking football 6-a-side', 6, 5, 'small', 1.2, six(1.2), { walking: true })
  ];
  var BY_ID = {};
  FORMATS.forEach(function (f) { BY_ID[f.id] = f; });
  // aliases so '11', '5', 'futsal' etc. also work
  ['11:eleven', '9:nine', '7:seven', '6:six', '5:five'].forEach(function (a) { a = a.split(':'); BY_ID[a[0]] = BY_ID[a[1]]; });

  function getFormat(id) { return BY_ID[id] || BY_ID.eleven; }
  function getFormation(formatId, formationId) {
    var F = getFormat(formatId);
    for (var i = 0; i < F.formations.length; i++) if (F.formations[i].id === formationId) return F.formations[i];
    return F.formations[0];
  }

  // absolute slot positions inside a box of width x height (default 780 x 758)
  function layoutSlots(formatId, formationId, opts) {
    opts = opts || {};
    var W = opts.width || REF_W, H = opts.height || REF_H, k = W / REF_W;
    var f = getFormation(formatId, formationId);
    return f.slots.map(function (sl) {
      return { x: f1(sl.x * W), y: f1(sl.y * H), s: f3(f.scale * (sl.s || 1) * k), role: sl.role, fine: sl.fine };
    });
  }

  var api = {
    FORMATS: FORMATS, PITCH_STYLES: PITCH_STYLES, PITCH_TYPES: PITCH_TYPES,
    BOX: { x: 60, y: 262, width: REF_W, height: REF_H },   // where the wizard puts the pitch on its 900 x 1200 print
    getFormat: getFormat, getFormation: getFormation, getStyle: getStyle,
    pitchSVG: pitchSVG, paperSVG: paperSVG, pitchGeometry: pitchGeometry, layoutSlots: layoutSlots,
    contrast: contrast
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.YourXIPitches = api;
})(this);
